import React, { useEffect, useMemo, useRef, useState } from "react";
import PartnerLayout from "@/components/PartnerLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Save, Eye, Rocket, Undo2, Trash2, EyeOff, GripVertical, X,
  Monitor, Smartphone, Palette, Search,
} from "lucide-react";
import PartnerLandingRenderer from "@/components/PartnerLandingRenderer";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BLOCK_LABELS = {
  hero: "Hero", features_grid: "Features", programs_showcase: "Programas",
  testimonials: "Testimonios", plans_teaser: "Planes teaser", stats_row: "Stats",
  cta_banner: "CTA Banner", logos_row: "Logos", rich_text: "Texto libre",
  image_full: "Imagen",
};

const FONT_OPTIONS = ["Inter", "Poppins", "Roboto", "Playfair Display"];

export default function LandingEditor() {
  const { user } = useAuth();
  const [landing, setLanding] = useState(null);
  const [blockTypes, setBlockTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewport, setViewport] = useState("desktop");
  const [showSettings, setShowSettings] = useState(false);
  const [programs, setPrograms] = useState({});
  const savingTimer = useRef(null);
  const lastSavedJson = useRef("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/partner/landing"),
        api.get("/partner/landing/block-types"),
      ]);
      setLanding(r1.data);
      setBlockTypes(r2.data.items);
      lastSavedJson.current = JSON.stringify(r1.data.blocks);
      // Hydrate programs
      const slugs = new Set();
      for (const b of r1.data.blocks || []) {
        if (b.type === "programs_showcase") {
          (b.props?.program_slugs || []).forEach((s) => slugs.add(s));
        }
      }
      if (slugs.size) {
        const map = {};
        await Promise.all(Array.from(slugs).map(async (s) => {
          try {
            const pr = await api.get(`/catalog/programs/${s}`);
            map[s] = pr.data;
          } catch (_) {}
        }));
        setPrograms(map);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Auto-save cada 30s si cambió
  useEffect(() => {
    if (!landing) return;
    const iv = setInterval(() => {
      const currJson = JSON.stringify(landing.blocks);
      if (currJson !== lastSavedJson.current) {
        saveDraft(true);
      }
    }, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [landing]);

  const selected = landing?.blocks?.find((b) => b.id === selectedId);

  const saveDraft = async (silent = false) => {
    if (!landing) return;
    setSaving(true);
    try {
      const r = await api.patch("/partner/landing", {
        blocks: landing.blocks, seo: landing.seo,
        branding_override: landing.branding_override || {},
      });
      setLanding((prev) => ({ ...prev, ...r.data }));
      lastSavedJson.current = JSON.stringify(r.data.blocks);
      if (!silent) toast.success("Borrador guardado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async (type) => {
    try {
      const r = await api.post("/partner/landing/blocks", { type });
      setLanding((prev) => ({ ...prev, blocks: [...(prev.blocks || []), r.data] }));
      setSelectedId(r.data.id);
      toast.success(`Bloque ${BLOCK_LABELS[type]} agregado`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateSelected = (patch) => {
    if (!selected) return;
    setLanding((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => b.id === selected.id ? { ...b, props: { ...(b.props || {}), ...patch } } : b),
    }));
  };

  const toggleVisible = (b) => {
    setLanding((prev) => ({
      ...prev,
      blocks: prev.blocks.map((x) => x.id === b.id ? { ...x, is_visible: !x.is_visible } : x),
    }));
  };

  const removeBlock = async (b) => {
    try {
      await api.delete(`/partner/landing/blocks/${b.id}`);
      setLanding((prev) => ({ ...prev, blocks: prev.blocks.filter((x) => x.id !== b.id) }));
      if (selectedId === b.id) setSelectedId(null);
      toast.success("Bloque eliminado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    setLanding((prev) => {
      const oldIdx = prev.blocks.findIndex((b) => b.id === active.id);
      const newIdx = prev.blocks.findIndex((b) => b.id === over.id);
      return { ...prev, blocks: arrayMove(prev.blocks, oldIdx, newIdx) };
    });
  };

  const publish = async () => {
    setSaving(true);
    try {
      // Save first
      await api.patch("/partner/landing", {
        blocks: landing.blocks, seo: landing.seo,
        branding_override: landing.branding_override || {},
      });
      const r = await api.post("/partner/landing/publish");
      setLanding((prev) => ({ ...prev, ...r.data.landing }));
      toast.success(`Publicado v${r.data.landing.version} · ${r.data.public_url}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const openPreview = async () => {
    try {
      // Save first
      await saveDraft(true);
      const r = await api.get("/partner/landing/preview-token");
      window.open(r.data.url, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revert = async () => {
    try {
      const r = await api.patch("/partner/landing", {}); // no-op to get fresh
      const r2 = await api.post("/partner/landing/revert");
      setLanding(r2.data);
      lastSavedJson.current = JSON.stringify(r2.data.blocks);
      toast.success("Revertido a la última versión publicada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading || !landing) {
    return (
      <PartnerLayout user={user}>
        <div className="h-96 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout user={user}>
      <div className="-m-4 md:-m-6 -mb-4 h-[calc(100vh-4rem)] flex flex-col bg-slate-100">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-slate-300 bg-white">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-bold">Landing builder</p>
            <p className="text-sm text-slate-700 font-semibold">
              {landing.tenant_name} ·{" "}
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${
                landing.status === "published"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                  : "bg-amber-100 text-amber-800 border-amber-300"
              }`}>
                {landing.status === "published" ? `Publicada v${landing.version}` : "Borrador"}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
              <button onClick={() => setViewport("desktop")} className={`p-1.5 ${viewport === "desktop" ? "bg-slate-100" : "bg-white"}`}><Monitor className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => setViewport("mobile")} className={`p-1.5 ${viewport === "mobile" ? "bg-slate-100" : "bg-white"}`}><Smartphone className="w-4 h-4 text-slate-600" /></button>
            </div>
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold"
            >
              <Palette className="w-3 h-3" /> Ajustes
            </button>
            <button
              type="button"
              onClick={() => saveDraft(false)}
              disabled={saving}
              data-testid="landing-editor-save-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold disabled:opacity-60"
            >
              <Save className="w-3 h-3" /> Guardar borrador
            </button>
            <button
              type="button"
              onClick={openPreview}
              data-testid="landing-preview-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold"
            >
              <Eye className="w-3 h-3" /> Vista previa
            </button>
            {landing.has_published_version && (
              <button
                type="button"
                onClick={revert}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs font-semibold"
              >
                <Undo2 className="w-3 h-3" /> Revertir
              </button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  disabled={saving}
                  data-testid="landing-editor-publish-btn"
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded bg-primary hover:brightness-110 text-white text-xs font-bold disabled:opacity-60"
                >
                  <Rocket className="w-3 h-3" /> Publicar
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Publicar landing?</AlertDialogTitle>
                  <AlertDialogDescription>
                    La versión actual del borrador se hará pública en <span className="font-mono">/l/{landing.tenant_slug}</span>.
                    Puedes revertir en cualquier momento.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={publish} className="bg-primary hover:brightness-110 text-white">
                    Publicar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* 3-panel layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: block library */}
          <aside className="w-64 border-r border-slate-300 bg-white overflow-y-auto p-4 flex-shrink-0">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Bloques</p>
            <div className="space-y-1.5">
              {blockTypes.map((bt) => (
                <button
                  key={bt.type}
                  type="button"
                  onClick={() => addBlock(bt.type)}
                  data-testid={`landing-editor-add-block-${bt.type}`}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 rounded border border-slate-200 bg-slate-50 hover:bg-white hover:border-primary text-xs font-semibold text-slate-700 transition-colors"
                >
                  <Plus className="w-3 h-3 text-primary" /> {BLOCK_LABELS[bt.type] || bt.type}
                </button>
              ))}
            </div>
            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Estructura ({landing.blocks.length}/30)</p>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={landing.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1" data-testid="landing-editor-block-list">
                    {landing.blocks.map((b) => (
                      <SortableBlockItem
                        key={b.id}
                        block={b}
                        selected={selectedId === b.id}
                        onSelect={() => setSelectedId(b.id)}
                        onToggleVisible={() => toggleVisible(b)}
                        onDelete={() => removeBlock(b)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </aside>

          {/* Center: live preview */}
          <main className="flex-1 overflow-y-auto bg-slate-200 p-4">
            <div className={`mx-auto bg-white shadow-2xl ${viewport === "mobile" ? "max-w-[400px]" : "max-w-full"} rounded overflow-hidden transition-all`} data-testid="landing-editor-preview-frame">
              <PartnerLandingRenderer
                landing={landing}
                programs={programs}
                editorMode
                onSelectBlock={setSelectedId}
                selectedBlockId={selectedId}
              />
            </div>
          </main>

          {/* Right: block property form */}
          <aside className="w-96 border-l border-slate-300 bg-white overflow-y-auto p-4 flex-shrink-0">
            {selected ? (
              <BlockPropsEditor
                key={selected.id}
                block={selected}
                onUpdate={updateSelected}
                onSave={() => saveDraft(false)}
                onDelete={() => removeBlock(selected)}
                onToggleVisible={() => toggleVisible(selected)}
              />
            ) : (
              <div className="text-center text-sm text-slate-400 py-12">
                Selecciona un bloque en el preview central o en la estructura para editarlo.
              </div>
            )}
          </aside>
        </div>

        {showSettings && (
          <SettingsPanel
            landing={landing}
            onChange={(patch) => setLanding((prev) => ({ ...prev, ...patch }))}
            onClose={() => { setShowSettings(false); saveDraft(true); }}
          />
        )}
      </div>
    </PartnerLayout>
  );
}

function SortableBlockItem({ block, selected, onSelect, onToggleVisible, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`block-struct-${block.id}`}
      onClick={onSelect}
      className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs cursor-pointer transition-colors ${
        selected
          ? "border-primary bg-primary/10"
          : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" onClick={(e) => e.stopPropagation()}>
        <GripVertical className="w-3 h-3" />
      </button>
      <span className="flex-1 truncate font-semibold text-slate-700">{BLOCK_LABELS[block.type] || block.type}</span>
      <button onClick={(e) => { e.stopPropagation(); onToggleVisible(); }} className="text-slate-400 hover:text-slate-700" title={block.is_visible === false ? "Mostrar" : "Ocultar"}>
        {block.is_visible === false ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-400 hover:text-red-600" title="Eliminar">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ---------------- Block editor forms (contextual, right panel) --------- */
function BlockPropsEditor({ block, onUpdate, onSave, onDelete, onToggleVisible }) {
  const t = block.type;
  const p = block.props || {};
  return (
    <div data-testid="landing-editor-block-props">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-widest text-primary font-bold">
          {BLOCK_LABELS[t] || t}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={onToggleVisible} className="p-1 text-slate-500 hover:text-slate-900" title="Toggle visibilidad">
            {block.is_visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button onClick={onDelete} className="p-1 text-red-500 hover:text-red-700" title="Eliminar">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {(t === "hero" || t === "cta_banner") && (
        <>
          {t === "hero" && <Field label="Eyebrow" value={p.eyebrow} onChange={(v) => onUpdate({ eyebrow: v })} />}
          <Field label="Título" value={p.title} onChange={(v) => onUpdate({ title: v })} />
          <TextareaField label="Subtítulo" value={p.subtitle} onChange={(v) => onUpdate({ subtitle: v })} />
          <Field label="CTA texto" value={p.cta_text} onChange={(v) => onUpdate({ cta_text: v })} />
          <Field label="CTA link (URL)" value={p.cta_link} onChange={(v) => onUpdate({ cta_link: v })} />
          {t === "hero" && (
            <>
              <Field label="Imagen fondo (URL)" value={p.background_image_url} onChange={(v) => onUpdate({ background_image_url: v })} />
              <SelectField label="Layout" value={p.layout} options={["left", "center"]} onChange={(v) => onUpdate({ layout: v })} />
            </>
          )}
          {t === "cta_banner" && (
            <SelectField label="Fondo" value={p.background_style} options={["gradient", "solid"]} onChange={(v) => onUpdate({ background_style: v })} />
          )}
        </>
      )}

      {t === "features_grid" && (
        <>
          <Field label="Título" value={p.heading} onChange={(v) => onUpdate({ heading: v })} />
          <ListEditor
            items={p.items || []}
            onChange={(items) => onUpdate({ items })}
            fields={["icon", "title", "description"]}
            max={8}
            testid="features"
          />
        </>
      )}

      {t === "programs_showcase" && (
        <>
          <Field label="Título" value={p.heading} onChange={(v) => onUpdate({ heading: v })} />
          <BoolField label="Mostrar precio" value={p.show_price} onChange={(v) => onUpdate({ show_price: v })} />
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-4 mb-2">Slugs de programa</p>
          <StringListEditor
            items={p.program_slugs || []}
            onChange={(program_slugs) => onUpdate({ program_slugs })}
            max={12}
            placeholder="high-performance-client-management-hpcm"
          />
        </>
      )}

      {t === "testimonials" && (
        <>
          <Field label="Título" value={p.heading} onChange={(v) => onUpdate({ heading: v })} />
          <ListEditor
            items={p.items || []}
            onChange={(items) => onUpdate({ items })}
            fields={["quote", "name", "role", "avatar_url"]}
            max={6}
            testid="testimonials"
          />
        </>
      )}

      {t === "plans_teaser" && (
        <>
          <Field label="Título" value={p.heading} onChange={(v) => onUpdate({ heading: v })} />
          <TextareaField label="Subtítulo" value={p.subtitle} onChange={(v) => onUpdate({ subtitle: v })} />
        </>
      )}

      {t === "stats_row" && (
        <ListEditor items={p.items || []} onChange={(items) => onUpdate({ items })} fields={["value", "label"]} max={5} testid="stats" />
      )}

      {t === "logos_row" && (
        <>
          <Field label="Título" value={p.heading} onChange={(v) => onUpdate({ heading: v })} />
          <ListEditor items={p.items || []} onChange={(items) => onUpdate({ items })} fields={["name"]} max={8} testid="logos" />
        </>
      )}

      {t === "rich_text" && (
        <TextareaField label="Markdown (max 4,000 chars)" value={p.body_md} onChange={(v) => onUpdate({ body_md: v })} rows={12} />
      )}

      {t === "image_full" && (
        <>
          <Field label="Imagen (URL)" value={p.image_url} onChange={(v) => onUpdate({ image_url: v })} />
          <Field label="Alt text" value={p.alt} onChange={(v) => onUpdate({ alt: v })} />
          <Field label="Caption" value={p.caption} onChange={(v) => onUpdate({ caption: v })} />
        </>
      )}

      <button
        onClick={onSave}
        className="mt-6 w-full py-2 rounded bg-slate-900 hover:bg-slate-700 text-white text-xs font-bold"
      >
        Guardar cambios
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div className="mb-3">
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</label>
      <input
        type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-primary"
      />
    </div>
  );
}
function TextareaField({ label, value, onChange, rows = 3 }) {
  return (
    <div className="mb-3">
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</label>
      <textarea
        rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-primary font-mono"
      />
    </div>
  );
}
function SelectField({ label, value, options, onChange }) {
  return (
    <div className="mb-3">
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</label>
      <select
        value={value || options[0]} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-primary"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function BoolField({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-2 mb-3 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      <span className="text-xs text-slate-700">{label}</span>
    </label>
  );
}

function ListEditor({ items, onChange, fields, max, testid }) {
  const add = () => { if (items.length < max) onChange([...items, Object.fromEntries(fields.map((f) => [f, ""]))]); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, f, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [f]: v } : it));
  return (
    <div className="mb-3" data-testid={`list-editor-${testid}`}>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="p-2 bg-slate-50 border border-slate-200 rounded relative">
            <button onClick={() => remove(i)} className="absolute top-1 right-1 text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
            {fields.map((f) => (
              <div key={f} className="mb-1">
                <label className="text-[9px] uppercase text-slate-400 font-bold">{f}</label>
                <input
                  value={it[f] || ""} onChange={(e) => update(i, f, e.target.value)}
                  className="mt-0.5 w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs text-slate-900 focus:outline-none focus:border-primary"
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      {items.length < max && (
        <button onClick={add} data-testid={`list-editor-add-${testid}`} className="mt-2 w-full py-1.5 rounded border border-dashed border-slate-300 text-slate-500 hover:border-primary hover:text-primary text-xs font-semibold">
          + Agregar item ({items.length}/{max})
        </button>
      )}
    </div>
  );
}

function StringListEditor({ items, onChange, max, placeholder }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim() && items.length < max) { onChange([...items, val.trim()]); setVal(""); } };
  return (
    <div className="mb-3">
      <div className="space-y-1">
        {items.map((s, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-1 bg-slate-50 border border-slate-200 rounded">
            <Search className="w-3 h-3 text-slate-400" />
            <span className="text-xs text-slate-700 font-mono flex-1 truncate">{s}</span>
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      {items.length < max && (
        <div className="flex items-center gap-1 mt-2">
          <input
            value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-xs text-slate-900 font-mono focus:outline-none focus:border-primary"
          />
          <button onClick={add} className="px-2 py-1 rounded bg-primary hover:brightness-110 text-white text-xs font-bold">+</button>
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ landing, onChange, onClose }) {
  const seo = landing.seo || {};
  const bo = landing.branding_override || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" data-testid="landing-settings-panel">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-bold">Ajustes generales</p>
            <h3 className="text-xl font-bold text-slate-900">SEO y branding</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <section className="mb-6">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">SEO</p>
          <Field label="Title" value={seo.title} onChange={(v) => onChange({ seo: { ...seo, title: v } })} />
          <TextareaField label="Description" value={seo.description} onChange={(v) => onChange({ seo: { ...seo, description: v } })} />
          <Field label="OG image URL" value={seo.og_image_url} onChange={(v) => onChange({ seo: { ...seo, og_image_url: v } })} />
        </section>
        <section>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Branding override (opcional · si vacío usa el del tenant)</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Color primario</label>
              <input type="color" value={bo.primary_color || "#DC5A0F"} onChange={(e) => onChange({ branding_override: { ...bo, primary_color: e.target.value } })} className="mt-1 w-full h-10 rounded border border-slate-300" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Color secundario</label>
              <input type="color" value={bo.secondary_color || "#F59E0B"} onChange={(e) => onChange({ branding_override: { ...bo, secondary_color: e.target.value } })} className="mt-1 w-full h-10 rounded border border-slate-300" />
            </div>
          </div>
          <Field label="Logo URL" value={bo.logo_url} onChange={(v) => onChange({ branding_override: { ...bo, logo_url: v } })} />
          <SelectField label="Fuente" value={bo.font_family || FONT_OPTIONS[0]} options={FONT_OPTIONS} onChange={(v) => onChange({ branding_override: { ...bo, font_family: v } })} />
        </section>
        <button onClick={onClose} className="mt-6 w-full py-2 rounded bg-primary hover:brightness-110 text-white text-sm font-bold">
          Guardar y cerrar
        </button>
      </div>
    </div>
  );
}
